import { Col, Form, Row, Stack } from 'react-bootstrap';
import type { PatientContactSectionField } from '../AdditionalSection/types';
import { Edit } from '../../../Icons';
import CareMapprIconButton from '../../../Button/CareMapprIconButton';
import { usePatientProfile } from '../../hooks';
import React from 'react';
import EpisodeDetailActionButtons from '../AdditionalSection/EpisodeDetailActionButtons';
import { formatPhone } from '../../utils';

interface PatientContactDetailsProps {
  patientContactConfig: PatientContactSectionField[];
  handleInputChange: (
    e: any,
    clinicIndex?: number,
    episodeIndex?: number,
  ) => void;
  clinics: any[];
  patientProfileStatus: any;
}

export const PatientContactLegacyEditActions = ({
  field,
  fieldKey,
  isEditing,
  actionBarHasEditButton,
  editProfile,
  onEditClick,
  onConfirmClick,
  onCancelClick,
}: {
  field: PatientContactSectionField;
  fieldKey: string;
  isEditing: boolean;
  actionBarHasEditButton: boolean;
  editProfile: boolean;
  onEditClick: (e: React.MouseEvent, fieldKey: string) => void;
  onConfirmClick: (fieldKey: string, e?: React.MouseEvent) => void;
  onCancelClick: (fieldKey: string, e?: React.MouseEvent) => void;
}) => (
  <>
    {!isEditing && !actionBarHasEditButton && field.editable && (
      <CareMapprIconButton
        className="m-0 p-0 min-width-auto"
        icon={<Edit size={14} />}
        onClick={(e) => onEditClick(e, fieldKey)}
      />
    )}
    {!editProfile && isEditing && (
      <EpisodeDetailActionButtons
        buttonClassName="m-0 p-0"
        onConfirm={(e) => onConfirmClick(fieldKey, e)}
        onCancel={(e) => onCancelClick(fieldKey, e)}
      />
    )}
  </>
);

const PatientContactDetails = ({
  patientContactConfig,
}: PatientContactDetailsProps) => {
  const { editProfile, patientProfile, actionBarGroup } = usePatientProfile();
  const { showEditButton: actionBarHasEditButton } = actionBarGroup;

  const primaryContacts = patientProfile?.primaryContacts ?? [];
  const [primaryContactsDraft, setPrimaryContactsDraft] =
    React.useState<any[]>(primaryContacts);
  const [currentEditingField, setCurrentEditingField] = React.useState<
    string[]
  >([]);

  React.useEffect(() => {
    setPrimaryContactsDraft(primaryContacts);
  }, [primaryContacts]);

  const onEditClick = (e: React.MouseEvent, fieldKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentEditingField((pre) => [...pre, fieldKey]);
  };

  const onCancelClick = (fieldKey: string, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setCurrentEditingField((pre) => pre.filter((item) => item !== fieldKey));
  };

  const onConfirmClick = (fieldKey: string, e?: React.MouseEvent) => {
    onCancelClick(fieldKey, e);
  };

  const setPrimaryContactValue = (
    contactIndex: number,
    contactKey: string,
    nextValue: any,
  ) => {
    setPrimaryContactsDraft((pre) =>
      pre.map((item, index) =>
        index === contactIndex ? { ...item, [contactKey]: nextValue } : item,
      ),
    );
  };

  const contactsToRender =
    primaryContactsDraft.length > 0 ? primaryContactsDraft : [{}];

  return (
    <Row className="mt-2">
      <span className="font-14 font-weight-600 my-2 roboto">
        Primary contact details
      </span>
      <Col xs={12}>
        {contactsToRender.map((primaryContact, contactIndex) => (
          <div key={`primary-contact-${contactIndex}`} className="w-100">
            <Row>
              {patientContactConfig?.map((field, index) => {
                const keys = field.key.split('.');
                const fieldKey = `${field.key}-${index}-${contactIndex}-patient-contact`;
                const contactKey = keys[1];
                const isEditing =
                  currentEditingField.includes(fieldKey) || editProfile;
                const primaryContactData = primaryContact as unknown as Record<
                  string,
                  any
                >;
                const originalPrimaryContactData = primaryContacts?.[
                  contactIndex
                ] as unknown as Record<string, any> | undefined;

                if (field.formatType === 'contactPhone') {
                  const phonesRaw = primaryContactData?.[contactKey];
                  const phones = Array.isArray(phonesRaw)
                    ? phonesRaw
                    : phonesRaw
                      ? [phonesRaw]
                      : [];
                  const originalPhonesRaw =
                    originalPrimaryContactData?.[contactKey];
                  const originalPhones = Array.isArray(originalPhonesRaw)
                    ? originalPhonesRaw
                    : originalPhonesRaw
                      ? [originalPhonesRaw]
                      : [];
                  return (
                    <Col
                      sm="4"
                      key={fieldKey}
                      className="d-flex align-items-start min-height-29"
                    >
                      <Stack
                        direction="horizontal"
                        gap={2}
                        className="labelRow align-items-start"
                      >
                        <Form.Label className="labelBold">
                          {field.title}
                        </Form.Label>
                        {isEditing && field.editable ? (
                          <div className="d-flex flex-column">
                            {(phones.length > 0 ? phones : ['']).map(
                              (phone, phoneIndex) => (
                                <Form.Control
                                  key={`${fieldKey}-phone-input-${phoneIndex}`}
                                  className="form-control-xs mb-1"
                                  type="text"
                                  value={phone ?? ''}
                                  onChange={(e) => {
                                    const next = [...phones];
                                    next[phoneIndex] = e.target.value;
                                    setPrimaryContactValue(
                                      contactIndex,
                                      contactKey,
                                      next,
                                    );
                                  }}
                                />
                              ),
                            )}
                          </div>
                        ) : phones.length > 0 ? (
                          <div className="d-flex flex-column">
                            {phones.map((phone, phoneIndex) => (
                              <Form.Label
                                key={`${fieldKey}-phone-${phoneIndex}`}
                                className="labelSimple"
                              >
                                {formatPhone(phone)}
                              </Form.Label>
                            ))}
                          </div>
                        ) : (
                          <Form.Label className="labelSimple">-</Form.Label>
                        )}
                        <PatientContactLegacyEditActions
                          field={field}
                          fieldKey={fieldKey}
                          isEditing={isEditing}
                          actionBarHasEditButton={actionBarHasEditButton}
                          editProfile={editProfile}
                          onEditClick={onEditClick}
                          onConfirmClick={onConfirmClick}
                          onCancelClick={(key, e) => {
                            setPrimaryContactValue(
                              contactIndex,
                              contactKey,
                              originalPhones,
                            );
                            onCancelClick(key, e);
                          }}
                        />
                      </Stack>
                    </Col>
                  );
                }

                const displayValue = primaryContactData?.[contactKey] || '-';
                const originalValue = originalPrimaryContactData?.[contactKey];
                return (
                  <Col
                    sm="4"
                    key={fieldKey}
                    className="d-flex align-items-center min-height-29"
                  >
                    <Stack direction="horizontal" gap={2} className="labelRow">
                      <Form.Label className="labelBold">
                        {field.title}
                      </Form.Label>
                      {isEditing && field.editable ? (
                        <Form.Control
                          className="form-control-xs"
                          type="text"
                          value={displayValue === '-' ? '' : displayValue}
                          onChange={(e) =>
                            setPrimaryContactValue(
                              contactIndex,
                              contactKey,
                              e.target.value,
                            )
                          }
                        />
                      ) : (
                        <Form.Label className="labelSimple">
                          {displayValue}
                        </Form.Label>
                      )}
                      <PatientContactLegacyEditActions
                        field={field}
                        fieldKey={fieldKey}
                        isEditing={isEditing}
                        actionBarHasEditButton={actionBarHasEditButton}
                        editProfile={editProfile}
                        onEditClick={onEditClick}
                        onConfirmClick={onConfirmClick}
                        onCancelClick={(key, e) => {
                          setPrimaryContactValue(
                            contactIndex,
                            contactKey,
                            originalValue,
                          );
                          onCancelClick(key, e);
                        }}
                      />
                    </Stack>
                  </Col>
                );
              })}
            </Row>
            {contactIndex < contactsToRender.length - 1 && (
              <div className="w-100 border-top my-2" />
            )}
          </div>
        ))}
      </Col>
    </Row>
  );
};

export default PatientContactDetails;
// editableText;
